import { Flex, List, VStack, useCallbackRef } from '@chakra-ui/react';
import {
  CustomSelectRenderItemContentType,
  CustomSelectStateProps,
} from '@monetize/types/app';
import { ChangeEvent, FC, memo, useCallback, useId } from 'react';
import { MBox } from '../../chakra';
import MCheckbox from '../../MCheckbox';
import MText from '../../MText';

interface CheckboxItemListProps extends Pick<
  CustomSelectStateProps,
  | 'isSelectedItem'
  | 'isDisabledItem'
  | 'isSubtitleItem'
  | 'itemRefs'
  | 'getTitle'
  | 'items'
  | 'cursor'
  | 'setCursor'
  | 'setCursorCurrent'
  | 'handleChange'
  | 'renderItemContent'
  | 'query'
  | 'omitNoResultsText'
> {
  indexOffset: number;
  rightLabel?: boolean;
}

interface CheckboxItemRowProps {
  item: any;
  index: number;
  title: string;
  listId: string;
  isHighlight: boolean;
  isSelected: boolean;
  isDisabled: boolean;
  isSubtitle: boolean;
  rightLabel?: boolean;
  renderItemContent?: CustomSelectRenderItemContentType;
  query: CheckboxItemListProps['query'];
  onRowEnter: (index: number) => void;
  onRowLeave: (index: number) => void;
  onItemChange: (item: any, event: ChangeEvent<HTMLInputElement>) => void;
  setItemRef: (index: number, element: HTMLInputElement | null) => void;
}

/**
 * One option. Memoized, with stable handlers from the list, because each v3
 * checkbox runs its own zag machine: re-rendering every row on each cursor
 * move made hovering and opening a long list (bulk add offerings) crawl.
 */
const CheckboxItemRow = memo(function CheckboxItemRow({
  item,
  index,
  title,
  listId,
  isHighlight,
  isSelected,
  isDisabled,
  isSubtitle,
  rightLabel,
  renderItemContent,
  query,
  onRowEnter,
  onRowLeave,
  onItemChange,
  setItemRef,
}: CheckboxItemRowProps) {
  const handleRef = useCallback(
    (element: HTMLInputElement | null) => setItemRef(index, element),
    [index, setItemRef],
  );

  return (
    <List.Item
      tabIndex={-1}
      _hover={{
        background: !isSubtitle ? 'tBlue.hover' : undefined,
      }}
      backgroundColor={isSelected || isHighlight ? 'tBlue.hover' : ''}
      cursor="pointer"
      // Keep the keyboard highlight in step with the mouse. Opening the list
      // puts it on the first selected row, and since it paints the same
      // background as hover, that row otherwise looks hovered until the
      // arrow keys move it, even after it's unselected.
      onMouseEnter={() => onRowEnter(index)}
      onMouseLeave={() => onRowLeave(index)}
      // Option rows put their padding on the checkbox instead, so its label
      // (the click target) fills the whole row, edges included.
      px={isSubtitle ? 1 : 0}
      py={isSubtitle ? 1 : 0}
      role="group"
      className="group"
      display={'flex'}
    >
      {isSubtitle ? (
        <MText
          disabled
          color={'tGray.disabledText'}
          lineClamp={1}
          cursor={'default'}
          pb={1}
          fontSize="sm"
          maxW="250px"
        >
          {title}
        </MText>
      ) : (
        <MCheckbox
          // Driven by the parent's own cursor/keyboard-nav state, not
          // native Tab order. `itemRefs` is typed for `HTMLDivElement`
          // (the sibling ItemList/CustomItemList variants ref a plain row
          // div), but MCustomSelect.tsx calls `.click()` on it for
          // keyboard selection, so the ref must stay on a real native
          // input, which is what MCheckbox forwards its ref to.
          tabIndex={-1}
          // Each option needs its own id: inside a Field (e.g.
          // SettingsControl) every option would otherwise take the field's
          // control id, the same one as the select's search input, so
          // clicking an option's label clicked that input instead.
          id={`${listId}-option-${index}`}
          ref={handleRef}
          isChecked={isSelected}
          isDisabled={isDisabled}
          onChange={(event) => {
            onItemChange(item, event);
          }}
          // An option is never itself required, but it inherits FormControl
          // context from an enclosing `MFormField isRequired`. Same reason as
          // the override in MainSearchInputV2.
          required={false}
          mt="0"
          px={1}
          py={3}
          w="100%"
          fontWeight={isSelected ? 'bold' : undefined}
          variant="custom-select"
          alignItems="center"
        >
          {renderItemContent ? (
            <Flex
              aria-disabled={isDisabled}
              aria-selected={isSelected}
              borderRadius="2"
              alignItems="center"
              maxWidth="100%"
            >
              {renderItemContent({
                title,
                item,
                isSubtitle: false,
                isSelected,
                isHighlight,
                isDisabled,
                query,
              })}
            </Flex>
          ) : (
            <Flex
              aria-disabled={isDisabled}
              aria-selected={isSelected}
              borderRadius="2"
              alignItems="center"
              justifyContent="space-between"
              maxWidth="100%"
            >
              <MText color={'tPurple.base'} lineClamp={1} fontSize="sm" ml="1">
                {title}
              </MText>
              {!!rightLabel && (
                <MText
                  fontSize="10px"
                  color="inherit"
                  _groupHover={{ color: 'tPurple.base' }}
                  ml="4"
                  fontWeight="400"
                >
                  {item.rightLabel}
                </MText>
              )}
            </Flex>
          )}
        </MCheckbox>
      )}
    </List.Item>
  );
});

const CheckboxItemList: FC<CheckboxItemListProps> = ({
  indexOffset,
  isSelectedItem,
  isDisabledItem,
  isSubtitleItem,
  itemRefs,
  getTitle,
  cursor,
  setCursor,
  setCursorCurrent,
  items,
  handleChange,
  renderItemContent,
  query,
  rightLabel,
  omitNoResultsText,
}: CheckboxItemListProps) => {
  const listId = useId();
  // The select recreates these every render; stable wrappers keep the rows'
  // memo intact while still calling the latest version.
  const onRowEnter = useCallbackRef((index: number) => setCursorCurrent(index));
  const onRowLeave = useCallbackRef((index: number) => {
    if (cursor === index) {
      setCursor(-1);
    }
  });
  const onItemChange = useCallbackRef(
    (item: any, event: ChangeEvent<HTMLInputElement>) => {
      handleChange(item, event);
    },
  );
  const setItemRef = useCallbackRef(
    (index: number, element: HTMLInputElement | null) => {
      itemRefs.current[index] = element as unknown as HTMLDivElement;
    },
  );

  if (items.length === 0) {
    return omitNoResultsText ? '' : <MBox p="2">No Results</MBox>;
  }

  return (
    <VStack align="left" gap="0">
      {items.map((item: any, i: number) => {
        const index = i + indexOffset;
        return (
          <CheckboxItemRow
            key={`cb-${index}`}
            item={item}
            index={index}
            title={getTitle(item)}
            listId={listId}
            isHighlight={cursor === index}
            isSelected={isSelectedItem(item)}
            isDisabled={isDisabledItem(item)}
            isSubtitle={isSubtitleItem(item)}
            rightLabel={rightLabel}
            renderItemContent={renderItemContent}
            query={query}
            onRowEnter={onRowEnter}
            onRowLeave={onRowLeave}
            onItemChange={onItemChange}
            setItemRef={setItemRef}
          />
        );
      })}
    </VStack>
  );
};

export default CheckboxItemList;
